import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SupportService } from '../services/support.service';
import { SpinnerService } from 'src/app/shared/services';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { EnergyCreditsAdminService } from '../energy-credits-admin-facilities/services/energy-credits-admin.service';
import { FacilityEnergyCredit } from '../energy-credits-admin-facilities/models/facility-energy-credits.model';

import { finalize, Subject, takeUntil } from 'rxjs';
import {
  MatRow,
  MatCell,
  MatHeaderRow,
  MatHeaderCell,
  MatTableModule,
} from '@angular/material/table';

@Component({
  selector: 'app-energy-credit-support',
  imports: [
    MatTableModule,
    MatRow,
    MatCell,
    MatHeaderRow,
    MatHeaderCell,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './energy-credit-support.component.html',
  styleUrl: './energy-credit-support.component.scss',
})
export class EnergyCreditSupportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private supportService: SupportService,
    private adminService: EnergyCreditsAdminService,
    private spinner: SpinnerService,
    public dialog: MatDialog,
    public dialogService: DialogService,
    private router: Router,
  ) {}

  facilities: FacilityEnergyCredit[] = [];
  selectedFacility: FacilityEnergyCredit | null = null;

  ngOnInit(): void {
    this.adminService
      .getFacilities()
      .pipe(takeUntil(this.destroy$))
      .subscribe((facilities) => {
        this.facilities = facilities
          .slice()
          .sort((a, b) =>
            a.KeyChain.LegacyId.localeCompare(b.KeyChain.LegacyId),
          );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayedColumns: string[] = ['task', 'action'];

  data = [
    {
      buttonLabel: 'Trigger',
      task: 'Trigger Energy Credit Submission Export',
      action: () => this.triggerSubmissionExport(),
    },
    {
      buttonLabel: 'Trigger',
      task: 'Trigger Heat Pump Invoice Sync',
      action: () => this.triggerHeatPumpInvoiceSync(),
    },
    {
      buttonLabel: 'Launch',
      task: 'Launch Energy Credits Facility Management',
      action: () => this.manageFacilityEligibility(),
    },
  ];

  triggerSubmissionExport() {
    this.supportService
      .getNumberOfExportableSubmissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        const formattedCount = new Intl.NumberFormat('en-us').format(count);
        const ref = this.dialogService.openDialog(
          'Confirm Submission Export',
          `This will submit approximately ${formattedCount} submissions for export. Do you want to proceed? This action cannot be undone.`,
          'Cancel',
          'Confirm',
          true,
        );
        ref
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((result) => {
            if (result) {
              this.spinner.show();
              this.supportService
                .triggerEnergyCreditSubmissionExport()
                .pipe(
                  finalize(() => this.spinner.hide()),
                  takeUntil(this.destroy$),
                )
                .subscribe();
            }
          });
      });
  }

  triggerHeatPumpInvoiceSync() {
    const ref = this.dialogService.openDialog(
      'Confirm Heat Pump Invoice Sync',
      `This will sync all submissions to OnBase to set their Heat Pump Invoice. Do you want to proceed? This action cannot be undone.`,
      'Cancel',
      'Confirm',
      true,
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.spinner.show();
          this.supportService
            .triggerHeatPumpInvoiceSync()
            .pipe(
              finalize(() => this.spinner.hide()),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }
      });
  }
  triggerClaytonGoToNextDataSync() {
    if (!this.selectedFacility) return;
    const { Name, KeyChain } = this.selectedFacility;
    const ref = this.dialogService.openDialog(
      'Confirm ClaytonGO to NEXT Data Sync',
      `This will trigger the ClaytonGO to NEXT Data Sync for facility ${KeyChain.LegacyId} - ${Name}. Do you want to proceed? This action cannot be undone.`,
      'Cancel',
      'Confirm',
      true,
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.spinner.show();
          this.supportService
            .triggerClaytonGoToNextDataSync(KeyChain.LegacyId)
            .pipe(
              finalize(() => this.spinner.hide()),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }
      });
  }
  manageFacilityEligibility() {
    this.router.navigate(['support/energy-credits-facilities']);
  }
}
